import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const SOURCE_SECTION_ID = 'recently-viewed-source';

/**
 * @typedef {Object} RecentlyViewedRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} count
 * @property {HTMLElement} progress
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 */

/**
 * Horizontal rail of products the shopper has already opened.
 * The current product is left out. The section stays hidden when nothing else qualifies.
 *
 * @extends {Component<RecentlyViewedRefs>}
 */
class RecentlyViewedProducts extends Component {
  requiredRefs = ['track', 'count', 'progress', 'previous', 'next'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.track.addEventListener('scroll', () => this.#updateControls(), { passive: true, signal });
    window.addEventListener('resize', () => this.#updateControls(), { signal });

    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {Event} event
   */
  scrollPrevious(event) {
    event.preventDefault();
    this.#scroll(-1);
  }

  /**
   * @param {Event} event
   */
  scrollNext(event) {
    event.preventDefault();
    this.#scroll(1);
  }

  async #load() {
    const max = Number(this.dataset.max) || 8;
    const currentId = this.dataset.productId || '';
    const ids = RecentlyViewed.getProducts()
      .filter((id) => id !== currentId)
      .slice(0, max);

    if (ids.length === 0) {
      if (this.dataset.designMode === 'true') {
        this.#updateControls();
      }

      return;
    }

    this.hidden = false;
    this.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');

      const html = await sectionRenderer.getSectionHTML(SOURCE_SECTION_ID, false, url);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = parsed.querySelectorAll('[data-recently-viewed-card]');

      if (cards.length === 0) {
        if (this.dataset.designMode !== 'true') this.hidden = true;
        this.removeAttribute('aria-busy');
        return;
      }

      this.refs.track.replaceChildren(...cards);
      this.refs.count.textContent = String(cards.length);
      this.removeAttribute('aria-busy');
      this.#updateControls();
    } catch (error) {
      console.error(error);
      if (this.dataset.designMode !== 'true') this.hidden = true;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {number} direction
   */
  #scroll(direction) {
    const card = this.refs.track.querySelector('[data-recently-viewed-card]');
    if (!card) return;

    const styles = getComputedStyle(this.refs.track);
    const gap = Number.parseFloat(styles.columnGap) || 0;
    const amount = card.getBoundingClientRect().width + gap;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.refs.track.scrollBy({
      left: direction * amount,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #updateControls() {
    const track = this.refs.track;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollLeft = Math.abs(track.scrollLeft);
    const canScroll = maxScroll > 2;

    this.refs.previous.disabled = !canScroll || scrollLeft <= 2;
    this.refs.next.disabled = !canScroll || scrollLeft >= maxScroll - 2;
    this.classList.toggle('recently-viewed--overflow', canScroll);

    const seen = maxScroll <= 0 ? 1 : (scrollLeft + track.clientWidth) / track.scrollWidth;
    this.refs.progress.style.transform = `scaleX(${Math.min(1, Math.max(0, seen))})`;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
